export type StoreListener<T> = (state: T, previous: T) => void;

export interface Store<T extends object> {
  get(): T;
  set(patch: Partial<T> | ((state: T) => Partial<T>)): void;
  subscribe(listener: StoreListener<T>, options?: { immediate?: boolean }): () => void;
  select<U>(selector: (state: T) => U, listener: (value: U, previous: U) => void, equals?: (a: U, b: U) => boolean): () => void;
}

export function createStore<T extends object>(initial: T): Store<T> {
  let state = initial;
  const listeners = new Set<StoreListener<T>>();
  let notifying = false;
  const queue: Array<[T, T]> = [];

  const notify = (next: T, previous: T): void => {
    queue.push([next, previous]);
    if (notifying) return;
    notifying = true;
    try {
      while (queue.length > 0) {
        const [n, p] = queue.shift()!;
        for (const l of Array.from(listeners)) {
          try {
            l(n, p);
          } catch (err) {
            console.error('store listener failed', err);
          }
        }
      }
    } finally {
      notifying = false;
    }
  };

  return {
    get: () => state,
    set(patch) {
      const resolved = typeof patch === 'function' ? patch(state) : patch;
      if (!resolved || Object.keys(resolved).length === 0) return;
      const previous = state;
      state = { ...state, ...resolved };
      notify(state, previous);
    },
    subscribe(listener, options) {
      listeners.add(listener);
      if (options?.immediate) listener(state, state);
      return () => listeners.delete(listener);
    },
    select(selector, listener, equals = Object.is) {
      let current = selector(state);
      listener(current, current);
      return this.subscribe((next) => {
        const value = selector(next);
        if (!equals(value, current)) {
          const previous = current;
          current = value;
          listener(value, previous);
        }
      });
    },
  };
}
